import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowSquareOut,
  Camera,
  CaretDown,
  DotsThree,
  Cube,
  FileArrowUp,
  FloppyDisk,
  ListNumbers,
  PencilRuler,
  Plus,
  Receipt,
  SquaresFour,
  Stack,
  Trash,
  Eraser,
  Waves,
  X,
  Package,
} from "@phosphor-icons/react";
import { CountertopDesigner, normalizePiece, SHAPES, CUTOUT_SIZE } from "@/components/CountertopDesigner";
import { SlabLayoutBoard } from "@/components/SlabLayoutBoard";
import { VeinMatchBoard } from "@/pages/VeinMatch";
import { Countertop3D } from "@/components/Countertop3D";
import { MaterialDrawer, materialCards, swatchStyle } from "@/components/MaterialDrawer";
import { LiveQuoteBar } from "@/components/LiveQuoteBar";
import { StudioStepBar, StudioStepFooter, SendToQuoteDialog } from "@/components/StudioShell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CustomerVeinProposal } from "@/components/CustomerVeinProposal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { QuoteStockDialog } from "@/components/QuoteStockDialog";
import { DxfLayerDialog } from "@/components/DxfLayerDialog";
import { StudioAiReviewDialog } from "@/components/StudioAiReviewDialog";
import { quoteSeams } from "@/lib/arrange3d";
import { money } from "@/lib/format";
import {
  areaMaterialPatch,
  materialOptionsFor,
  movePieceIn,
  mergeSeamIn,
  newArea,
  splitAreaPieceIn,
  uploadTracePhoto,
} from "@/lib/quoteAreas";
import { useUndoHistory } from "@/lib/useUndoHistory";

const TABS = [
  {
    key: "draw",
    label: "Draw",
    icon: PencilRuler,
    hint: "Dimensions, shape, edges, cutouts",
  },
  {
    key: "layout",
    label: "Slab layout",
    icon: SquaresFour,
    hint: "Nest the pieces on slabs",
  },
  {
    key: "vein",
    label: "Vein match",
    icon: Waves,
    hint: "Slide pieces on the slab photo",
  },
  {
    key: "3d",
    label: "3D view",
    icon: Cube,
    hint: "Walk around the finished counters",
  },
];

const QuotePanel = ({ totals, quoteId, onClose }) => {
  const lines = (totals?.lines || []).filter((l) => !l.removed && !l.hidden);
  return (
    <aside
      data-testid="studio-quote-panel"
      className="fixed inset-y-0 right-0 z-30 flex w-[min(20rem,90vw)] flex-col border-l border-zinc-800 bg-zinc-900 shadow-2xl lg:static lg:z-auto lg:w-80 lg:shrink-0 lg:bg-zinc-900/60 lg:shadow-none"
    >
      <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-3">
        <Receipt size={15} weight="bold" className="text-indigo-300" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
          Live quote
        </span>
        <button
          type="button"
          data-testid="studio-quote-close"
          onClick={onClose}
          className="ml-auto rounded-md p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-100"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
        <div
          className="font-mono-data text-3xl font-bold tracking-tight text-zinc-50"
          data-testid="studio-total"
        >
          {money(totals?.total || 0)}
        </div>
        <div className="mt-1 text-xs text-zinc-500">
          {totals?.raw_sqft || 0} ft² drawn · {totals?.billed_sqft || 0} ft²
          billed
          {Number(totals?.waste_sqft) > 0
            ? ` · ${totals.waste_sqft} ft² waste`
            : ""}
        </div>

        <div className="mt-5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
          Areas
        </div>
        <div className="mt-2 space-y-1.5">
          {(totals?.areas || []).map((a) => (
            <div
              key={a.id}
              data-testid={`studio-area-total-${a.id}`}
              className="flex items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-xs"
            >
              <div className="min-w-0">
                <div className="truncate font-semibold text-zinc-200">
                  {a.name}
                </div>
                <div className="truncate text-[11px] text-zinc-500">
                  {a.sqft} ft² · {a.slab_color || "No material"}
                </div>
              </div>
              <span className="font-mono-data text-zinc-100">
                {money(a.subtotal)}
              </span>
            </div>
          ))}
          {!totals?.areas?.length && (
            <p className="text-xs text-zinc-500">
              Draw a piece to see pricing.
            </p>
          )}
        </div>

        {lines.length > 0 && (
          <>
            <div className="mt-5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Lines
            </div>
            <div
              className="mt-2 divide-y divide-zinc-800/80"
              data-testid="studio-lines"
            >
              {lines.slice(0, 60).map((l, i) => (
                <div
                  key={l.key || l.id || i}
                  className="flex items-start justify-between gap-2 py-1.5 text-xs"
                >
                  <div className="min-w-0">
                    <div className="truncate text-zinc-200">
                      {l.item || l.description || "Line"}
                    </div>
                    <div className="font-mono-data text-[10px] text-zinc-500">
                      {l.qty} {l.unit} × {money(l.unit_price)}
                    </div>
                  </div>
                  <span className="font-mono-data shrink-0 text-zinc-100">
                    {money(l.total)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="border-t border-zinc-800 px-4 py-3 text-xs">
        {[
          ["Subtotal", totals?.subtotal],
          totals?.trade_discount
            ? ["Trade discount", -totals.trade_discount]
            : null,
          totals?.discount ? ["Discount", -totals.discount] : null,
          ["Tax", totals?.tax],
        ]
          .filter(Boolean)
          .map(([k, v]) => (
            <div key={k} className="flex justify-between py-0.5 text-zinc-400">
              <span>{k}</span>
              <span className="font-mono-data">{money(v || 0)}</span>
            </div>
          ))}
        <div className="mt-1 flex justify-between border-t border-zinc-800 pt-2 text-sm font-bold text-zinc-50">
          <span>Total</span>
          <span className="font-mono-data">{money(totals?.total || 0)}</span>
        </div>
        <Link
          to={`/quotes/${quoteId}?step=3`}
          data-testid="studio-open-quote"
          className="mt-3 flex items-center justify-center gap-1.5 rounded-full border border-indigo-400/40 bg-indigo-500/10 py-2 text-[10px] font-bold uppercase tracking-widest text-indigo-200 hover:bg-indigo-500/20"
        >
          Edit lines on the quote <ArrowSquareOut size={12} weight="bold" />
        </Link>
      </div>
    </aside>
  );
};

export default function DesignStudio() {
  const { id } = useParams();
  useEffect(() => {
    document.body.classList.add("studio-open");
    return () => document.body.classList.remove("studio-open");
  }, []);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab"))
    ? params.get("tab")
    : "draw";
  const setTab = (t) => setParams({ tab: t }, { replace: true });
  const goReview = async () => {
    const ok = await save(true);
    if (ok) navigate(`/quotes/${id}?step=4`);
  };
  const [sendOpen, setSendOpen] = useState(false);

  const [q, setQ] = useState(null);
  const layoutHistory = useUndoHistory(q, setQ);
  const [meta, setMeta] = useState({
    rates: {},
    edge_profiles: [],
    edge_styles: [],
    cutout_kinds: [],
    cutout_colors: {},
    drawing_general: {},
  });
  const [sinks, setSinks] = useState([]);
  const [services, setServices] = useState([]);
  const [cutTemplates, setCutTemplates] = useState([]);
  const [customLayouts, setCustomLayouts] = useState([]);
  const putCustomLayouts = async (items, msg) => {
    setCustomLayouts(items);
    try {
      await api.put("/settings/custom-layouts", { items });
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error("Could not save the layout tile");
    }
  };
  const setCutTemplatePhoto = async (tid, photo) => {
    const items = cutTemplates.map((x) => (x.id === tid ? { ...x, photo } : x));
    setCutTemplates(items);
    await api.put("/settings/cutout-templates", { items }).catch(() => {});
  };
  const [savedLayouts, setSavedLayouts] = useState([]);
  const [rateBands, setRateBands] = useState({});
  const [slabStock, setSlabStock] = useState([]);
  const [slabsAll, setSlabsAll] = useState([]);
  const [remnants, setRemnants] = useState([]);
  const [colors, setColors] = useState([]);
  const [totals, setTotals] = useState(null);
  const [areaId, setAreaId] = useState("");
  const [busy, setBusy] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);
  const [dxfLayersOpen, setDxfLayersOpen] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [canvasOnly, setCanvasOnly] = useState(false);
  const [quoteBarOpen, setQuoteBarOpen] = useState(false);
  const [matOpen, setMatOpen] = useState(false);
  const [missing, setMissing] = useState(false);
  const savedRef = useRef("");

  const load = useCallback(async () => {
    try {
      const [p, cat, sk, ct, sl, rm, qt, sv, dl, rb] = await Promise.all([
        api.get("/settings/pricing"),
        api.get("/inventory/catalog"),
        api.get("/sinks"),
        api.get("/settings/cutout-templates"),
        api.get("/slabs"),
        api.get("/remnants"),
        api.get(`/quotes/${id}`),
        api.get("/services").catch(() => ({ data: [] })),
        api.get("/settings/drawing-layouts").catch(() => ({ data: [] })),
        api.get("/settings/rate-bands").catch(() => ({ data: {} })),
      ]);
      setServices(sv.data || []);
      setMeta({
        rates: p.data.pricing?.groups || {},
        materialRates: p.data.pricing?.material_rates || {},
        edge_profiles: p.data.edge_profiles || [],
        edge_styles: p.data.edge_styles || [],
        cutout_kinds: p.data.cutout_kinds || [],
        cutout_colors: p.data.cutout_colors || {},
        cutout_sizes: p.data.cutout_sizes || {},
        drawing_general: p.data.drawing_general || {},
        drawing_kits: p.data.drawing_kits || [],
        drawing_lines: p.data.drawing_lines || {},
      });
      setColors(cat.data.slabs || []);
      setSinks(
        (sk.data || []).map((s) => ({
          id: s.id,
          label: [s.brand, s.model].filter(Boolean).join(" "),
          qty: s.quantity,
          sink_type: s.sink_type,
          size: s.size || "",
          cutout_size: s.cutout_size || "",
          price: Number(s.sale_price ?? s.cost ?? 0),
        })),
      );
      setCutTemplates(ct.data || []);
      setSavedLayouts(dl.data || []);
      api.get("/settings/custom-layouts").then((r) => setCustomLayouts(r.data || [])).catch(() => {});
      setRateBands(rb.data || {});
      setSlabsAll(sl.data || []);
      setSlabStock((sl.data || []).filter((s) => s.status === "In Stock"));
      setRemnants((rm.data || []).filter((r) => r.status === "Available"));
      const data = qt.data;
      if (!data.areas?.length)
        data.areas = [{ ...newArea(1), name: "Kitchen" }];
      setQ(data);
      setTotals(data.totals || null);
      savedRef.current = JSON.stringify(data);
      setAreaId(data.areas[0].id);
    } catch {
      setMissing(true);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = !!q && JSON.stringify(q) !== savedRef.current;

  // the 3D hero picture is saved by its own endpoint, so keep both copies in step
  const withHero = (hero) => (cur) => {
    const design = { ...(cur.design || {}) };
    if (hero) design.hero = hero;
    else delete design.hero;
    return { ...cur, design };
  };

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const save = useCallback(
    async (silent = false) => {
      if (!q) return null;
      const body = {
        ...q,
        design: { ...(q.design || {}), seams: quoteSeams(q) },
        project_name:
          (q.project_name || "").trim() ||
          `Design ${new Date().toLocaleDateString()}`,
      };
      setBusy(true);
      try {
        const { data } = await api.put(`/quotes/${id}`, body);
        setQ(data);
        setTotals(data.totals || null);
        savedRef.current = JSON.stringify(data);
        if (!silent) toast.success("Design saved");
        return data;
      } catch (err) {
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
        );
        return null;
      } finally {
        setBusy(false);
      }
    },
    [q, id],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "s") return;
      e.preventDefault();
      save();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  const sendNestToQuote = async (line) => {
    if (!q) return;
    const lid = `stoneex-nest-${line.area_id || ""}`;
    const lines = [
      ...(q.lines || []).filter((e) => e.id !== lid),
      { id: lid, manual: true, hidden: false, ...line },
    ];
    const body = {
      ...q,
      lines,
      design: { ...(q.design || {}), seams: quoteSeams(q) },
      project_name: (q.project_name || "").trim() || `Design ${new Date().toLocaleDateString()}`,
    };
    try {
      const { data } = await api.put(`/quotes/${id}`, body);
      setQ(data);
      setTotals(data.totals || null);
      savedRef.current = JSON.stringify(data);
      toast.success("Added to quote");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update the quote");
    }
  };

  const priceKey = q
    ? JSON.stringify([
        q.areas,
        q.lines,
        q.discount,
        q.discount_pct,
        q.trade_account,
        q.customer_group,
        q.slab_color,
        q.stone_group,
        q.thickness,
      ])
    : "";
  useEffect(() => {
    if (!q) return undefined;
    let stale = false;
    const t = setTimeout(() => {
      api
        .post("/quotes/price", q)
        .then(({ data }) => !stale && setTotals(data))
        .catch(() => {});
    }, 350);
    return () => {
      stale = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceKey]);

  const materialOptions = useMemo(
    () => materialOptionsFor(slabStock, remnants, colors),
    [slabStock, remnants, colors],
  );
  const matCards = useMemo(
    () => materialCards(slabStock, remnants, colors, meta.rates),
    [slabStock, remnants, colors, meta.rates],
  );

  const setArea = (aid, patch) =>
    setQ((cur) => ({
      ...cur,
      areas: cur.areas.map((a) => (a.id === aid ? { ...a, ...patch } : a)),
    }));

  const addArea = () => {
    const a = newArea((q?.areas?.length || 0) + 1);
    setQ((cur) => ({ ...cur, areas: [...cur.areas, a] }));
    setAreaId(a.id);
  };

  const removeArea = (aid) => {
    const a = q.areas.find((x) => x.id === aid);
    if (!a || q.areas.length < 2) return;
    if (
      (a.pieces || []).length &&
      !window.confirm(`Remove ${a.name} and its ${a.pieces.length} pieces?`)
    )
      return;
    const rest = q.areas.filter((x) => x.id !== aid);
    setQ((cur) => ({ ...cur, areas: rest }));
    setAreaId(rest[0].id);
  };

  const deleteDrawing = (aid) => {
    const a = q.areas.find((x) => x.id === aid);
    if (!a) return;
    if (
      !window.confirm(
        `Delete the drawing for ${a.name || "this area"}? Pieces, walls, notes and the trace go — the area, its material and quote lines stay.`,
      )
    )
      return;
    setQ((cur) => {
      const gone = new Set((a.pieces || []).map((p) => p.id));
      const layout = cur.layout || {};
      const placements = Object.fromEntries(
        Object.entries(layout.placements || {}).filter(([pid]) => !gone.has(pid)),
      );
      const areas = cur.areas.map((x) =>
        x.id !== aid ? x : { ...x, pieces: [], walls: [], notes: [], trace: {} },
      );
      const anyLeft = areas.some((x) => (x.pieces || []).length);
      return {
        ...cur,
        areas,
        layout: { ...layout, placements },
        ...(anyLeft ? {} : { drawing_approval: {} }),
      };
    });
    toast.success("Drawing deleted");
  };

  const splitAreaPiece = (aid, pieceId, axis, at) => {
    const next = splitAreaPieceIn(q, aid, pieceId, axis, at);
    if (!next) {
      toast.error("That cut misses the piece");
      return;
    }
    layoutHistory.remember();
    setQ(next);
    toast.success("Seam cut — piece split in two");
  };

  const mergeSeam = (aid, pieceId) => {
    const next = mergeSeamIn(q, aid, pieceId);
    if (!next) {
      toast.error("Couldn't weld that seam — the other half isn't lined up with it");
      return;
    }
    layoutHistory.remember();
    setQ(next);
    toast.success("Seam removed — pieces joined back together");
  };

  const setPortalFlag = async (patch) => {
    setQ((cur) => ({ ...cur, ...patch }));
    try {
      await api.patch(`/quotes/${id}/portal-flags`, patch);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not change that",
      );
    }
  };

  const uploadTrace = async (file) => {
    try {
      return await uploadTracePhoto(file);
    } catch {
      toast.error("Could not upload that picture");
      return null;
    }
  };

  const dxfRef = useRef(null);
  const [dragDxf, setDragDxf] = useState(false);
  const dragDepth = useRef(0);
  const isDxfName = (name) => /\.(dxf|xdf)$/i.test(name || "");

  const photoRef = useRef(null);
  const edgeFromType = (t) => {
    if (t === "wall") return { profile: "Unfinished", seam: false };
    if (t === "seam") return { profile: "Eased", seam: true };
    if (t === "fridge")
      return { profile: "Eased", seam: false, appliance: "fridge" };
    if (t === "stove" || t === "range")
      return { profile: "Eased", seam: false, appliance: "range" };
    if (t === "dishwasher" || t === "dw")
      return { profile: "Eased", seam: false, appliance: "dishwasher" };
    if (t === "hood" || t === "range hood" || t === "vent")
      return { profile: "Eased", seam: false, appliance: "hood" };
    return { profile: "Eased", seam: false };
  };
  const SLAB_USABLE_LEN = 120; // inches — flag runs longer than a standard slab
  const aiCutoutKind = (raw) => {
    const k = (raw || "").toLowerCase();
    if (k.includes("farmhouse") || k.includes("apron")) return "Farmhouse sink";
    if (k.includes("drop")) return "Drop-in sink";
    if (k.includes("sink")) return "Undermount sink";
    if (k.includes("cook") || k.includes("range") || k.includes("stove"))
      return "Cooktop";
    if (k.includes("faucet")) return "Faucet hole";
    if (k.includes("soap")) return "Soap dispenser";
    if (k.includes("outlet")) return "Outlet";
    return "Undermount sink";
  };
  const mapCutout = (c, len, dep) => {
    const kind = aiCutoutKind(c.kind);
    const [dw, dh] = CUTOUT_SIZE[kind] || [24, 18];
    const w = Number(c.width_in) > 0 ? Number(c.width_in) : dw;
    const h = Number(c.height_in) > 0 ? Number(c.height_in) : dh;
    const cx = Number(c.center_x_in);
    const cy = Number(c.center_y_in);
    const rawX =
      cx > 0 ? Math.round(cx - w / 2) : Math.round(len / 2 - w / 2);
    const rawY =
      cy > 0 ? Math.round(cy - h / 2) : Math.round(dep / 2 - h / 2);
    const round = ["Faucet hole", "Soap dispenser"].includes(kind);
    return {
      id: Math.random().toString(36).slice(2, 9),
      kind,
      w,
      h,
      x: Math.min(Math.max(2, rawX), Math.max(2, Math.round(len - w - 2))),
      y: Math.min(Math.max(2, rawY), Math.max(2, Math.round(dep - h - 2))),
      rot: 0,
      shape: round ? "Round" : "Rectangle",
      sink_type: kind === "Farmhouse sink" ? "Farmhouse" : "Undermount",
      faucet_holes: kind.toLowerCase().includes("sink") ? 1 : 0,
      ai_unconfirmed: true,
    };
  };
  const [aiReview, setAiReview] = useState(null);
  const importPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/studio/analyze-drawing", fd);
      if (!(data.pieces || []).length) {
        toast.error("No pieces could be read from that photo");
      } else {
        setAiReview({ pieces: data.pieces, notes: data.notes || "", file });
      }
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not read that drawing",
      );
    }
    setBusy(false);
  };
  const addAiPieces = (pieces) => {
    setAiReview(null);
    {
      const rid = () => Math.random().toString(36).slice(2, 9);
      let flagged = 0;
      const built = (pieces || []).map((p, i) => {
        const shapeKey = SHAPES[p.shape] ? p.shape : "Rectangle";
        const len = Number(p.length_in) || 96;
        const dep = Number(p.depth_in) || 25.5;
        const pts = SHAPES[shapeKey](len, dep);
        const edges = pts.map((_, idx) => edgeFromType((p.edges || [])[idx]));
        if (p.guessed) flagged += 1;
        const upright = p.type === "backsplash" || p.type === "waterfall";
        const cutouts = (p.cutouts || []).map((c) => mapCutout(c, len, dep));
        const seamSug = (p.seams || [])
          .map((s) => ({
            id: rid(),
            dir: s.dir === "h" ? "h" : "v",
            at: Number(s.at_in) || 0,
            reason: s.reason || "",
          }))
          .filter((s) => s.at > 0 && s.at < (s.dir === "h" ? dep : len));
        if (!seamSug.length && len > SLAB_USABLE_LEN) {
          seamSug.push({
            id: rid(),
            dir: "v",
            at: Math.round(len / 2),
            reason: "Run longer than a slab",
          });
        }
        const piece = normalizePiece({
          id: rid(),
          label: `${p.guessed ? "⚠ " : ""}${p.label || `Piece ${i + 1}`}`,
          points: pts,
          edges,
          cutouts,
          seam_suggestions: seamSug,
          x: 4,
          y: 4 + i * 8,
          rot: 0,
          backsplash_sqft: 0,
          orientation: upright ? "vertical" : "horizontal",
          stand: p.type === "waterfall" ? "waterfall" : "backsplash",
        });
        return { room: (p.room || "").trim(), piece };
      });
      if (!built.length) {
        toast.error("Nothing to add");
        return;
      }
      const cutCount = built.reduce(
        (n, b) => n + (b.piece.cutouts?.length || 0),
        0,
      );
      const seamCount = built.reduce(
        (n, b) => n + (b.piece.seam_suggestions?.length || 0),
        0,
      );
      const byRoom = new Map();
      built.forEach(({ room, piece }) => {
        const key = room || "__current";
        if (!byRoom.has(key)) byRoom.set(key, []);
        byRoom.get(key).push(piece);
      });
      const roomNames = [...byRoom.keys()].filter((k) => k !== "__current");
      setQ((cur) => {
        let areas = [...cur.areas];
        byRoom.forEach((ps, key) => {
          if (key === "__current") {
            const tid = areas.some((a) => a.id === areaId)
              ? areaId
              : areas[0]?.id;
            areas = areas.map((a) =>
              a.id === tid ? { ...a, pieces: [...(a.pieces || []), ...ps] } : a,
            );
          } else {
            let a = areas.find(
              (x) => (x.name || "").trim().toLowerCase() === key.toLowerCase(),
            );
            if (!a) {
              a = { ...newArea(areas.length + 1), name: key };
              areas = [...areas, a];
            }
            areas = areas.map((x) =>
              x.id === a.id
                ? { ...x, pieces: [...(x.pieces || []), ...ps] }
                : x,
            );
          }
        });
        return { ...cur, areas };
      });
      if (tab !== "draw") setTab("draw");
      const total = built.length;
      toast.success(
        `${total} piece${total > 1 ? "s" : ""} read from photo` +
          (roomNames.length > 1 ? ` · ${roomNames.length} rooms` : "") +
          (flagged ? ` · ${flagged} to check` : "") +
          (cutCount ? ` · ${cutCount} cutout${cutCount > 1 ? "s" : ""}` : "") +
          (seamCount ? ` · ${seamCount} seam tip${seamCount > 1 ? "s" : ""}` : ""),
      );
    }
  };

  const importDxfFile = async (file) => {
    if (!isDxfName(file.name)) {
      toast.error(`${file.name} is not a .dxf or .xdf template`);
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/quotes/dxf/preview", fd);
      const rid = () => Math.random().toString(36).slice(2, 9);
      const pieces = (data.pieces || []).map((p) => ({
        id: rid(),
        label: p.label,
        shape: "poly",
        length_in: p.length_in,
        width_in: p.width_in,
        points: p.points,
        edges: p.edges,
        cutouts: p.cutouts || [],
        x: p.x || 0,
        y: p.y || 0,
        backsplash_sqft: 0,
      }));
      setQ((cur) => {
        const tid = cur.areas.some((a) => a.id === areaId)
          ? areaId
          : cur.areas[0]?.id;
        return {
          ...cur,
          areas: cur.areas.map((a) =>
            a.id === tid
              ? { ...a, pieces: [...(a.pieces || []), ...pieces] }
              : a,
          ),
        };
      });
      if (tab !== "draw") setTab("draw");
      const nLayers = (data.layers || []).length;
      toast.success(
        `${pieces.length} pieces read from ${file.name}` +
          (nLayers ? ` · ${nLayers} layer${nLayers > 1 ? "s" : ""} mapped` : ""),
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not read that file",
      );
    }
    setBusy(false);
  };
  const importDxf = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) importDxfFile(file);
  };
  const hasFiles = (e) =>
    Array.from(e.dataTransfer?.types || []).includes("Files");
  const onDragEnter = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragDxf(true);
  };
  const onDragOver = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (e) => {
    if (!hasFiles(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragDxf(false);
  };
  const onDrop = async (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragDxf(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    const templates = files.filter((f) => isDxfName(f.name));
    if (!templates.length) {
      toast.error("Drop a .dxf or .xdf template file");
      return;
    }
    for (const f of templates) await importDxfFile(f);
  };

  if (missing)
    return (
      <div className="grain flex min-h-screen items-center justify-center p-8 text-center">
        <div>
          <p className="text-sm text-zinc-400">
            That quote could not be opened.
          </p>
          <Link
            to="/design"
            className="mt-3 inline-block text-xs uppercase tracking-widest text-indigo-300"
          >
            Back to Design Studio
          </Link>
        </div>
      </div>
    );
  if (!q)
    return (
      <div
        className="grain flex min-h-screen items-center justify-center text-sm text-zinc-500"
        data-testid="studio-loading"
      >
        Opening the studio…
      </div>
    );

  const area = q.areas.find((a) => a.id === areaId) || q.areas[0];
  const pieceCount = (a) =>
    (a.pieces || []).filter((p) => !p.panel_kind).length;
  const areaColor = area.slab_color || q.slab_color || "";
  const areaCard = matCards.find((c) => c.name === areaColor) || null;
  const edgeSummary = [
    ...new Set(
      (area.pieces || [])
        .filter((p) => !p.panel_kind)
        .flatMap((p) => (p.edges || []).filter((e) => !e.seam).map((e) => e.profile || "Unfinished")),
    ),
  ].join(" · ");

  return (
    <div
      data-testid="design-studio"
      className="grain studio-shell flex h-screen flex-col overflow-hidden bg-zinc-950 text-zinc-100"
    >
      {!canvasOnly && (
      <header className="no-scrollbar relative z-20 flex h-14 shrink-0 items-center gap-2 overflow-x-auto border-b border-zinc-800/80 bg-zinc-950/90 px-3 backdrop-blur-md sm:gap-3 sm:px-4 [&>*]:shrink-0">
        <Link
          to={`/quotes/${id}?step=0`}
          data-testid="studio-back"
          className="flex items-center gap-1.5 rounded-full border border-zinc-800 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-400 transition-colors hover:border-zinc-700 hover:text-white"
        >
          <ArrowLeft size={13} weight="bold" /> <span className="hidden sm:inline">Quote</span>
        </Link>
        <div className="hidden min-w-0 items-center gap-2 md:flex">
          <span className="font-mono-data text-xs text-indigo-300">
            {q.code}
          </span>
          <span
            className="truncate text-sm font-semibold text-zinc-200"
            data-testid="studio-title"
          >
            {q.project_name || "Untitled design"}
          </span>
          {dirty && (
            <span
              data-testid="studio-dirty"
              title="Unsaved changes"
              className="h-2 w-2 shrink-0 rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.8)]"
            />
          )}
        </div>

        <div className="mx-auto">
          <StudioStepBar steps={TABS} current={tab} onPick={setTab} onReview={goReview} />
        </div>

        <input
          ref={dxfRef}
          data-testid="studio-dxf-input"
          type="file"
          accept=".dxf,.xdf"
          className="hidden"
          onChange={importDxf}
        />
        <input
          ref={photoRef}
          data-testid="studio-photo-input"
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={importPhoto}
        />
        {aiReview && (
          <StudioAiReviewDialog
            review={aiReview}
            onClose={() => setAiReview(null)}
            onAdd={addAiPieces}
          />
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="studio-import-menu"
              disabled={busy}
              title="Import, slab stock and more"
              className="order-last flex h-9 items-center gap-2 rounded-full border border-zinc-800 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors hover:border-indigo-400/50 hover:bg-zinc-800 hover:text-white disabled:opacity-40"
            >
              <DotsThree size={16} weight="bold" />
              <span className="hidden sm:inline">More</span>
              <CaretDown size={11} weight="bold" className="hidden sm:inline" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            data-testid="studio-import-menu-content"
            className="w-64 border-zinc-800 bg-zinc-900"
          >
            <DropdownMenuItem
              data-testid="studio-import-dxf"
              onClick={() => dxfRef.current?.click()}
            >
              <FileArrowUp size={15} weight="bold" className="mr-2" />
              <span>
                DXF / XDF template
                <span className="block text-[10px] text-zinc-500">
                  or drag the file onto the drawing
                </span>
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="studio-import-photo"
              onClick={() => photoRef.current?.click()}
            >
              <Camera size={15} weight="bold" className="mr-2" />
              <span>
                Photo of a sketch
                <span className="block text-[10px] text-zinc-500">
                  AI reads shapes, sizes and edges
                </span>
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-zinc-800" />
            <DropdownMenuItem
              data-testid="studio-dxf-layers"
              onClick={() => setDxfLayersOpen(true)}
            >
              <Stack size={15} weight="bold" className="mr-2" />
              <span>
                DXF layer mapping
                <span className="block text-[10px] text-zinc-500">
                  Edge, profile, seam, cut-out or ignore per layer
                </span>
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-zinc-800" />
            <DropdownMenuItem
              data-testid="studio-stock"
              onClick={() => setStockOpen(true)}
            >
              <Package size={15} weight="bold" className="mr-2" />
              <span>
                Slab stock
                <span className="block text-[10px] text-zinc-500">
                  Reserve or check slabs for this quote
                </span>
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Link
          to={`/quotes/${id}`}
          data-testid="studio-open-quote"
          title="Open the quote review"
          className="hidden h-9 items-center gap-2 rounded-full border border-zinc-800 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800 hover:text-white md:flex"
        >
          <Receipt size={14} weight="bold" />
          <span className="hidden sm:inline">Quote</span>
        </Link>
        <QuoteStockDialog quoteId={id} open={stockOpen} onClose={() => setStockOpen(false)} />
        <DxfLayerDialog
          open={dxfLayersOpen}
          onClose={() => setDxfLayersOpen(false)}
          edgeProfiles={meta.edge_profiles}
        />
        <button
          type="button"
          data-testid="studio-save"
          disabled={busy}
          onClick={() => save()}
          className={`flex h-9 items-center gap-2 rounded-full border px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 disabled:opacity-40 ${
            dirty
              ? "border-amber-400/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
              : "border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-white"
          }`}
        >
          <FloppyDisk size={14} weight="bold" />
          <span className="hidden sm:inline">{busy ? "Saving…" : dirty ? "Save" : "Saved"}</span>
        </button>
      </header>
      )}

      <div
        className="relative z-10 flex min-h-0 flex-1"
        data-testid="studio-dropzone"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {dragDxf && (
          <div
            data-testid="studio-dxf-drop-overlay"
            className="pointer-events-none absolute inset-3 z-50 flex items-center justify-center rounded-2xl border-2 border-dashed border-indigo-400/80 bg-zinc-950/85 backdrop-blur-sm"
          >
            <div className="flex flex-col items-center gap-3 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-indigo-400/40 bg-indigo-500/15 text-indigo-200">
                <FileArrowUp size={30} weight="bold" />
              </span>
              <div className="text-lg font-bold tracking-tight text-white">
                Drop your DXF / XDF template
              </div>
              <div className="text-xs uppercase tracking-widest text-zinc-400">
                Pieces import into{" "}
                <span className="text-indigo-200">
                  {area.name || "this area"}
                </span>
              </div>
            </div>
          </div>
        )}
        <section
          key={tab}
          className={`studio-pane min-w-0 flex-1 ${canvasOnly ? "p-0" : "p-3"} ${tab === "draw" ? "flex flex-col overflow-hidden" : "overflow-auto"}`}
          data-testid={`studio-pane-${tab}`}
        >
          {tab === "draw" && (
            <>
              <div
                className={`studio-ribbon mb-3 flex shrink-0 flex-wrap items-center gap-2 ${canvasOnly ? "hidden" : ""}`}
                data-testid="studio-area-strip"
              >
                {q.areas.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`studio-area-${a.id}`}
                    onClick={() => setAreaId(a.id)}
                    className={`flex h-9 items-center gap-2 rounded-full border px-3 text-xs font-semibold transition-colors ${
                      a.id === area.id
                        ? "border-amber-400/60 bg-amber-500/15 text-amber-100"
                        : "border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-100"
                    }`}
                  >
                    {a.name || "Area"}
                    <span className="font-mono-data text-[10px] text-zinc-500">
                      {pieceCount(a)}
                    </span>
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="studio-add-area"
                  onClick={addArea}
                  className="flex h-9 items-center gap-1 rounded-full border border-dashed border-zinc-700 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:border-indigo-400 hover:text-indigo-200"
                >
                  <Plus size={12} weight="bold" /> Area
                </button>
                <span className="mx-1 hidden h-6 w-px bg-zinc-800 md:block" />
                <button
                  type="button"
                  data-testid="studio-area-material"
                  onClick={() => setMatOpen(true)}
                  title="Choose the material for this area"
                  className="flex h-10 items-center gap-2.5 rounded-full border border-zinc-800 bg-zinc-950 pl-1.5 pr-4 text-left transition-colors duration-150 hover:border-indigo-400/50"
                >
                  <span
                    className="h-7 w-7 shrink-0 rounded-full border border-zinc-700 bg-cover bg-center"
                    style={swatchStyle(areaCard)}
                  />
                  <span className="min-w-0">
                    <span
                      className="block max-w-[14rem] truncate text-xs font-semibold text-zinc-100"
                      data-testid="studio-area-material-name"
                    >
                      {areaColor || "Choose material"}
                    </span>
                    <span className="block text-[10px] text-zinc-500">
                      {areaCard
                        ? [areaCard.thickness, areaCard.kind === "slab" ? `${areaCard.count} in stock` : areaCard.kind]
                            .filter(Boolean)
                            .join(" · ")
                        : areaColor
                          ? "not in inventory"
                          : "browse swatches"}
                    </span>
                  </span>
                </button>
                {q.areas.length > 1 && (
                  <button
                    type="button"
                    data-testid="studio-remove-area"
                    onClick={() => removeArea(area.id)}
                    title="Remove this area"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-zinc-800 text-zinc-500 hover:border-red-500/40 hover:text-red-400"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                )}
                {((area.pieces || []).length > 0 ||
                  (area.walls || []).length > 0 ||
                  (area.notes || []).length > 0 ||
                  area.trace?.path) && (
                  <button
                    type="button"
                    data-testid="studio-delete-drawing"
                    onClick={() => deleteDrawing(area.id)}
                    title="Delete this area's drawing (keeps the area, material and quote lines)"
                    className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-800 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:border-amber-400/50 hover:text-amber-200"
                  >
                    <Eraser size={13} weight="bold" /> Delete drawing
                  </button>
                )}
                <button
                  type="button"
                  data-testid="studio-quote-toggle"
                  onClick={() => setQuoteBarOpen((v) => !v)}
                  title="Live quote details"
                  className={`ml-auto hidden h-10 items-center gap-2 rounded-full border px-3 transition-colors duration-150 lg:flex ${
                    quoteBarOpen
                      ? "border-indigo-400/50 bg-indigo-500/10"
                      : "border-zinc-800 bg-zinc-950 hover:border-indigo-400/40"
                  }`}
                >
                  <Receipt size={14} weight="bold" className="text-indigo-300" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Live quote
                  </span>
                  <span
                    className="font-mono-data text-sm font-bold text-zinc-50"
                    data-testid="studio-quote-toggle-total"
                  >
                    {money(totals?.total || 0)}
                  </span>
                  <CaretDown
                    size={12}
                    weight="bold"
                    className={`text-zinc-500 transition-transform duration-200 ${quoteBarOpen ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
              <div
                className={`${quoteBarOpen ? "mb-3 shrink-0" : "shrink-0"} ${canvasOnly ? "hidden" : ""}`}
                data-testid="studio-quote-drawer"
                data-open={quoteBarOpen ? "1" : "0"}
              >
                <LiveQuoteBar
                  totals={totals}
                  areaId={area.id}
                  material={areaColor ? `${areaColor}${areaCard?.thickness ? ` ${areaCard.thickness}` : ""}` : ""}
                  edges={edgeSummary}
                  pieces={pieceCount(area)}
                  open={showQuote}
                  onOpen={() => setShowQuote((v) => !v)}
                  showBar={quoteBarOpen}
                />
                {quoteBarOpen && (
                  <Link
                    to={`/quotes/${id}?step=3`}
                    data-testid="studio-quote-lines"
                    className="mt-2 hidden items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-indigo-300 hover:text-indigo-200 lg:inline-flex"
                  >
                    <ListNumbers size={13} weight="bold" /> Open line items
                  </Link>
                )}
              </div>
              <div className="min-h-0 flex-1">
                <CountertopDesigner
                  key={area.id}
                  pieces={area.pieces || []}
                  edgeProfiles={meta.edge_profiles}
                  edgeStyles={meta.edge_styles}
                  general={meta.drawing_general}
                  cutoutKits={meta.drawing_kits}
                  onChange={(pieces) => setArea(area.id, { pieces })}
                  areaName={area.name}
                  onAreaName={(name) => setArea(area.id, { name })}
                  areas={q.areas.map((a) => ({ id: a.id, name: a.name }))}
                  currentAreaId={area.id}
                  onMovePiece={(pieceId, target) =>
                    setQ((cur) => movePieceIn(cur, pieceId, target))
                  }
                  cutoutKinds={meta.cutout_kinds}
                  cutoutColors={meta.cutout_colors}
                  cutoutSizes={meta.cutout_sizes}
                  lineWeight={meta.drawing_lines?.line_weight}
                  products={sinks}
                  services={services}
                  cutoutTemplates={cutTemplates}
                  onSetCutoutTemplatePhoto={setCutTemplatePhoto}
                  customLayouts={customLayouts}
                  onSaveCustomLayout={(l) => putCustomLayouts([...customLayouts, l], `${l.label} added to layouts`)}
                  onDeleteCustomLayout={(lid) => putCustomLayouts(customLayouts.filter((x) => x.id !== lid), "Layout tile removed")}
                  walls={area.walls || []}
                  onWallsChange={(w) => setArea(area.id, { walls: w })}
                  notes={area.notes || []}
                  onNotesChange={(n) => setArea(area.id, { notes: n })}
                  canvasOnly={canvasOnly}
                  onCanvasOnly={setCanvasOnly}
                  trace={area.trace?.path ? area.trace : null}
                  onTraceChange={(t) => setArea(area.id, { trace: t || {} })}
                  onUploadTrace={uploadTrace}
                  hiddenSteps={["layout"]}
                  materialMissing={!areaColor}
                  onPickMaterial={() => setMatOpen(true)}
                  onOpenQuote={() => setQuoteBarOpen(true)}
                  onSendToQuote={sendNestToQuote}
                  savedLayouts={savedLayouts}
                  liveTotal={totals?.total}
                  expectedRate={totals?.group_rate}
                  billedSqft={totals?.billed_sqft}
                  rateBand={rateBands?.[q?.stone_group]}
                  stoneGroup={area?.stone_group || q?.stone_group || ""}
                  onGoToLayout={() => setTab("layout")}
                  onSaveLayout={async (l) => {
                    const items = [...savedLayouts, l];
                    setSavedLayouts(items);
                    await api
                      .put("/settings/drawing-layouts", { items })
                      .catch(() => {});
                    toast.success(`${l.name} saved`);
                  }}
                  onDeleteLayout={async (lid) => {
                    const items = savedLayouts.filter((x) => x.id !== lid);
                    setSavedLayouts(items);
                    await api
                      .put("/settings/drawing-layouts", { items })
                      .catch(() => {});
                  }}
                  onRenameLayout={async (lid, name) => {
                    const items = savedLayouts.map((x) =>
                      x.id === lid ? { ...x, name } : x,
                    );
                    setSavedLayouts(items);
                    await api
                      .put("/settings/drawing-layouts", { items })
                      .catch(() => {});
                    toast.success("Layout renamed");
                  }}
                  onSaveCutoutTemplate={async (t) => {
                    const items = [...cutTemplates, t];
                    setCutTemplates(items);
                    await api
                      .put("/settings/cutout-templates", { items })
                      .catch(() => {});
                    toast.success(`${t.name} saved`);
                  }}
                  onDeleteCutoutTemplate={async (tid) => {
                    const items = cutTemplates.filter((x) => x.id !== tid);
                    setCutTemplates(items);
                    await api
                      .put("/settings/cutout-templates", { items })
                      .catch(() => {});
                  }}
                  onRenameCutoutTemplate={async (tid, name) => {
                    const items = cutTemplates.map((x) => (x.id === tid ? { ...x, name } : x));
                    setCutTemplates(items);
                    await api
                      .put("/settings/cutout-templates", { items })
                      .catch(() => {});
                    toast.success("Setup renamed");
                  }}
                  canvasHeight="100%"
                  onReview={goReview}
                />
              </div>
            </>
          )}

          {tab === "layout" && (
            <SlabLayoutBoard
              areas={q.areas}
              thickness={q.thickness}
              quoteColor={q.slab_color}
              layout={q.layout || {}}
              onLayoutChange={(l) => setQ((cur) => ({ ...cur, layout: l }))}
              onBeforeChange={layoutHistory.remember}
              onUndo={layoutHistory.undo}
              undoCount={layoutHistory.undoCount}
              onMergeSeam={mergeSeam}
              materialOptions={materialOptions}
              onAreaMaterial={(aid, v) => setArea(aid, areaMaterialPatch(v))}
              onAddArea={addArea}
              onSplitPiece={splitAreaPiece}
              rateFor={(color) => {
                const mr =
                  meta.materialRates?.[String(color || "").trim().toLowerCase()];
                if (Number(mr) > 0) return Number(mr);
                const a = q.areas.find(
                  (x) => (x.slab_color || q.slab_color) === color,
                );
                return Number(
                  meta.rates?.[a?.stone_group || q.stone_group] || 0,
                );
              }}
              inventorySlabs={slabStock}
              quoteId={id && id !== "new" ? id : ""}
              onBackToDrawing={() => setTab("draw")}
              onSave={() => save()}
              saving={busy}
              onVeinMatch={() => setTab("vein")}
              portalShown={q.show_layout_in_portal !== false}
              onPortalShown={(v) => setPortalFlag({ show_layout_in_portal: v })}
              canvasHeight="calc(100vh - 210px)"
            />
          )}

          {tab === "vein" && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
              <CustomerVeinProposal quote={q} onApplied={(data) => setQ(data)} />
              <VeinMatchBoard
                quote={q}
                layout={q.layout || {}}
                onLayoutChange={(l) => setQ((cur) => ({ ...cur, layout: l }))}
                onOpenDrawing={() => setTab("layout")}
                onPieceMove={(areaId, pieceId, x, y) =>
                  setQ((cur) => ({
                    ...cur,
                    areas: (cur.areas || []).map((a) =>
                      a.id !== areaId
                        ? a
                        : { ...a, pieces: (a.pieces || []).map((p) => (p.id === pieceId ? { ...p, x, y } : p)) },
                    ),
                  }))
                }
                compact
              />
            </div>
          )}

          {tab === "3d" && (
            <Countertop3D
              quote={q}
              slabStock={slabsAll}
              scene={q.design?.scene}
              onSceneChange={(s) =>
                setQ((cur) => ({
                  ...cur,
                  design: { ...(cur.design || {}), scene: s },
                }))
              }
              height="calc(100vh - 132px)"
              portalShown={q.show_3d_in_portal !== false}
              onPortalShown={(v) => setPortalFlag({ show_3d_in_portal: v })}
                  onArrangeChange={(a) =>
                setQ((cur) => ({
                  ...cur,
                  design: { ...(cur.design || {}), arrange: a },
                }))
              }
              onApplyToDrawing={(aid, pieces) => setArea(aid, { pieces })}
              edgeStyles={meta.edge_styles}
              onHero={(hero) => {
                setQ(withHero(hero));
                if (savedRef.current)
                  savedRef.current = JSON.stringify(
                    withHero(hero)(JSON.parse(savedRef.current)),
                  );
              }}
            />
          )}
          {tab !== "draw" && tab !== "vein" && (
            <StudioStepFooter
              steps={TABS}
              current={tab}
              onPick={setTab}
              onReview={() => setSendOpen(true)}
              total={totals?.total}
              saving={busy}
            />
          )}
        </section>

        <SendToQuoteDialog
          open={sendOpen}
          onClose={() => setSendOpen(false)}
          onConfirm={async () => {
            setSendOpen(false);
            await goReview();
          }}
          quote={q}
          totals={totals}
          saving={busy}
        />
        {showQuote && (
          <QuotePanel
            totals={totals}
            quoteId={id}
            onClose={() => setShowQuote(false)}
          />
        )}
      </div>
      <MaterialDrawer
        open={matOpen}
        onClose={() => setMatOpen(false)}
        cards={matCards}
        value={areaColor}
        areaName={area.name}
        onPick={(v) => setArea(area.id, areaMaterialPatch(v))}
      />
    </div>
  );
}
